import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';

import { SectionWrapper } from '../components/SectionWrapper';

import styles from './WhatItIsSection.module.css';

interface PillarCard {
  body: string;
  label: string;
  /** A follow-on page for readers who want to see the pillar in practice. */
  link?: { href: string; label: string };
  title: string;
}

const cards: PillarCard[] = [
  {
    body:
      'KAI uses the actual content of your Kudu course - the textbook, the worked examples, the homework framing - as ground truth. When a student asks about Chapter 4, KAI is reading Chapter 4. The answers come from the same content the student is being taught from, in the same notation and with the same conventions.',
    label: '01 - COURSE-AWARE',
    title: 'Grounded in your textbook, not the open internet.',
  },
  {
    body:
      'Faculty write the system prompts that tell KAI how to behave, and the default behavior is to ask leading questions, give partial hints, and walk students toward an answer they construct themselves. KAI will not solve a homework question outright. It will teach a student how to.',
    label: '02 - DIDACTIC, NOT SOLUTION-DUMP',
    link: { href: '/kai-coach', label: 'See it in Kai Coach' },
    title: 'Guides students through the problem instead of doing it for them.',
  },
  {
    body:
      "Every student in a Kudu course gets the same KAI capability: frontier models, full features, no rate limits during normal study. Students who can't afford ChatGPT Plus get the same tutor as students who can. AI access becomes a leveler in the classroom rather than another form of inequality.",
    label: '03 - EQUITABLE BY DEFAULT',
    title: 'Premium AI for every student. No pro tier gating.',
  },
  {
    body:
      'Student queries are anonymized before they reach a model provider. We use providers under contractual no-training guarantees. We never sell, share, or use queries to train anything. Your data leaves Kudu only as anonymized text and the answer comes back the same way.',
    label: '04 - PRIVACY BY ARCHITECTURE',
    title: 'Anonymized at the routing layer. No training on student data.',
  },
];

export function WhatItIsSection() {
  return (
    <SectionWrapper background="white" data-testid="ai-section-what-it-is">
      <div className={styles.section}>
        <div className={styles.header}>
          <p className={styles.eyebrow}>WHAT IT IS</p>
          <h2 className={styles.title}>
            A tutor that&apos;s read your textbook. And only your textbook.
          </h2>
          <p className={styles.subhead}>
            Most AI assistants are general-purpose tools your students will use
            whether you assign them or not. KAI is the alternative - built into
            Kudu courses, scoped to the actual course content, and designed by
            educators to help students learn rather than help them avoid
            learning.
          </p>
        </div>

        <div className={styles.grid}>
          {cards.map((card) => (
            <article className={styles.card} key={card.label}>
              <span className={styles.label}>{card.label}</span>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              <p className={styles.body}>{card.body}</p>
              {card.link ? (
                <Link className={styles.cardLink} to={card.link.href}>
                  {card.link.label}
                  <ArrowRight aria-hidden="true" size={14} />
                </Link>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
}
